import { safeStorage } from "@/utils/safeStorage";

export function getPlatform() {
  // Try modern userAgentData API first
  if ("userAgentData" in navigator) {
    const uaData = navigator.userAgentData as unknown as {
      platform: string;
      mobile: boolean;
    };

    // iOS detection using maxTouchPoints
    if (
      uaData.platform === "macOS" &&
      "maxTouchPoints" in navigator &&
      navigator.maxTouchPoints > 2
    ) {
      return "iOS";
    }

    // Direct platform mapping
    const platformMap: Record<string, string | undefined> = {
      macOS: "macOS",
      Windows: "Windows",
      Android: "Android",
      iOS: "iOS",
    };

    const detectedPlatform = platformMap[uaData.platform];
    if (detectedPlatform) {
      return detectedPlatform;
    }
  }

  // Fallback to traditional methods
  const { userAgent } = window.navigator;

  // iOS detection
  if (/iPhone|iPad|iPod/.test(userAgent)) {
    return "iOS";
  }

  // iPadOS 13+ ships a desktop-class "Macintosh" UA; the touch surface is the
  // only reliable tell (Apple's own recommended check). Without this, iPads
  // take every desktop code path — e.g. the staged-video preview renders the
  // desktop #t= first-frame <video>, which paints nothing on WebKit (blank
  // tile), and the iPad PWA loses the home-indicator spacer. NOTE: the
  // userAgentData branch above has the same check, but it never runs on
  // WebKit (Safari doesn't implement userAgentData).
  if (/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 2) {
    return "iOS";
  }

  // macOS detection
  if (/Macintosh|Mac OS X/.test(userAgent)) {
    return "macOS";
  }

  // Windows detection
  if (/Windows NT/.test(userAgent)) {
    return "Windows";
  }

  // Android detection
  if (/Android/.test(userAgent)) {
    return "Android";
  }

  return undefined; // If no OS is detected
}

// Check for OS Environments
export const PLATFORM_ENV = getPlatform();
export const IS_MAC_OS = PLATFORM_ENV === "macOS";
export const IS_WINDOWS = PLATFORM_ENV === "Windows";
export const IS_IOS = PLATFORM_ENV === "iOS";
export const IS_ANDROID = PLATFORM_ENV === "Android";
export const IS_MOBILE = IS_IOS || IS_ANDROID;
export const IS_SAFARI = /^((?!chrome|android).)*safari/i.test(
  navigator.userAgent,
);
export const IS_CHROME_ON_IOS = /CriOS/i.test(navigator.userAgent);
export const IS_CHROME =
  /chrome|chromium|crios/i.test(navigator.userAgent) && !IS_SAFARI;
export const IS_FIREFOX = /firefox|iceweasel|icecat/i.test(navigator.userAgent);

// Check if the browser is supported for PWA installation (Chrome or Safari)
export const IS_SUPPORTED_BROWSER = IS_SAFARI || IS_CHROME || IS_CHROME_ON_IOS;

// Determine if the app is running as a PWA
export const IS_PWA =
  // Check if display mode is standalone (most reliable for Android PWAs)
  window.matchMedia("(display-mode: standalone)").matches ||
  // iOS-specific: Check navigator.standalone (non-standard, Safari only)
  // Indicates if the app is launched from the home screen on iOS
  (window.navigator as { standalone?: boolean }).standalone ||
  // Check if the app was opened via an Android deep link
  // (less common, used when the app is opened from another Android app)
  document.referrer.includes("android-app://");
// Check if the app is already installed as a PWA or if it's not installable
export const IS_IOS_INSTALLABLE = IS_IOS && !IS_PWA;
// Check if service workers are supported and the app is in a secure context
export const IS_SERVICE_WORKER_SUPPORTED =
  "serviceWorker" in navigator &&
  (window.location.protocol === "https:" ||
    window.location.hostname === "localhost");

// Check for additional PWA-related features
export const IS_CACHING_SUPPORTED = "caches" in window;
export const IS_PUSH_SUPPORTED = "PushManager" in window;
export const IS_PROGRESSIVE_SUPPORTED =
  IS_SERVICE_WORKER_SUPPORTED && IS_CACHING_SUPPORTED && IS_PUSH_SUPPORTED;
export const IS_INSTALL_PROMPT_SUPPORTED = "onbeforeinstallprompt" in window;

// Check if environment primarily supports touch interactions
export const IS_TOUCH_ENV =
  window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window;

// Check for support of the Origin Private File System (OPFS) API
export const IS_OPFS_SUPPORTED = Boolean(navigator.storage?.getDirectory);

if (IS_OPFS_SUPPORTED) {
  // Clear old contents periodically (e.g., once per session)
  (async () => {
    try {
      const lastCleanup = safeStorage.get("lastCleanup");
      const now = Date.now();

      // Check if we haven't cleaned up in the last day
      if (!lastCleanup || now - Number(lastCleanup) > 86400000) {
        const directory = await navigator.storage.getDirectory();
        try {
          await directory.removeEntry("downloads", { recursive: true });
        } catch (error) {
          // "downloads" usually doesn't exist (nothing in the app creates it
          // anymore) — that's a successful cleanup, not a failure. Before this
          // guard the NotFoundError also skipped the timestamp write below, so
          // the sweep re-ran and re-logged "OPFS Cleanup failed" on EVERY boot.
          if (
            !(error instanceof DOMException && error.name === "NotFoundError")
          ) {
            throw error;
          }
        }
        safeStorage.set("lastCleanup", now.toString());
      }
    } catch (error) {
      // Log in development mode for debugging
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error("OPFS Cleanup failed:", error);
      }
    }
  })();
}

export const IS_BACKDROP_BLUR_SUPPORTED =
  CSS.supports("backdrop-filter: blur()") ||
  CSS.supports("-webkit-backdrop-filter: blur()");

export const ARE_WEBCODECS_SUPPORTED =
  "VideoDecoder" in window && "VideoEncoder" in window;

// to be moved to a constant config file
export const MAX_BUFFER_SIZE =
  (IS_MOBILE !== undefined ? (IS_MOBILE ? 512 : 2000) : 2000) * 1024 ** 2;
